// src/store/slices/authSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { User } from '../../types/user';
import { authService } from '../../services/authService';

export interface AuthState {
	user: User | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	error: string | null;
}

const initialState: AuthState = {
	user: null,
	isAuthenticated: false,
	isLoading: false,
	error: null,
};

// Async thunks
export const loginUser = createAsyncThunk(
	'auth/login',
	async ({ email, password, rememberMe = false }: { email: string; password: string; rememberMe?: boolean }) => {
		const response = await authService.login(email, password, rememberMe);
		return response;
	}
);

export const registerUser = createAsyncThunk(
	'auth/register',
	async ({ email, password, name }: { email: string; password: string; name: string }) => {
		const response = await authService.register(email, password, name);
		return response;
	}
);

export const logoutUser = createAsyncThunk(
	'auth/logout',
	async (_, { rejectWithValue }) => {
		try {
			await authService.logout();
			// Return success - components can then handle navigation
			return;
		} catch (error) {
			return rejectWithValue(error instanceof Error ? error.message : 'Logout failed');
		}
	}
);

export const getCurrentUser = createAsyncThunk(
	'auth/getCurrentUser',
	async () => {
		const user = await authService.getCurrentUser();
		return user;
	}
);

export const forgotPassword = createAsyncThunk(
	'auth/forgotPassword',
	async (email: string) => {
		await authService.forgotPassword(email);
		return email;
	}
);

export const resetPassword = createAsyncThunk(
	'auth/resetPassword',
	async ({ token, newPassword }: { token: string; newPassword: string }) => {
		await authService.resetPassword(token, newPassword);
	}
);

const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		clearError: (state) => {
			state.error = null;
		},
		setUser: (state, action: PayloadAction<User | null>) => {
			state.user = action.payload;
			state.isAuthenticated = !!action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			// Login
			.addCase(loginUser.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(loginUser.fulfilled, (state, action) => {
				state.isLoading = false;
				state.user = action.payload.user;
				state.isAuthenticated = true;
				state.error = null;
			})
			.addCase(loginUser.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.error.message || 'Login failed';
			})
			// Register
			.addCase(registerUser.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(registerUser.fulfilled, (state, action) => {
				state.isLoading = false;
				state.user = action.payload.user;
				state.isAuthenticated = true;
				state.error = null;
			})
			.addCase(registerUser.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.error.message || 'Registration failed';
			})
			// Logout
			.addCase(logoutUser.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(logoutUser.fulfilled, (state) => {
				state.isLoading = false;
				state.user = null;
				state.isAuthenticated = false;
				state.error = null;
			})
			.addCase(logoutUser.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.error.message || 'Logout failed';
			})
			// Get current user
			.addCase(getCurrentUser.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(getCurrentUser.fulfilled, (state, action) => {
				state.isLoading = false;
				state.user = action.payload;
				state.isAuthenticated = !!action.payload;
				state.error = null;
			})
			.addCase(getCurrentUser.rejected, (state, action) => {
				state.isLoading = false;
				state.user = null;
				state.isAuthenticated = false;
				state.error = action.error.message || 'Failed to get current user';
			})
			// Forgot password
			.addCase(forgotPassword.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(forgotPassword.fulfilled, (state) => {
				state.isLoading = false;
				state.error = null;
			})
			.addCase(forgotPassword.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.error.message || 'Failed to send reset email';
			})
			// Reset password
			.addCase(resetPassword.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(resetPassword.fulfilled, (state) => {
				state.isLoading = false;
				state.error = null;
			})
			.addCase(resetPassword.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.error.message || 'Failed to reset password';
			});
	},
});

export const { clearError, setUser } = authSlice.actions;
export default authSlice.reducer;